import { Component, OnInit, inject, computed, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { MessageService } from 'primeng/api';
import { LoadingOverlayComponent } from '../../../shared/loading-overlay.component';
import { CitaDialogComponent } from '../../cita/container/cita-dialog.component';
import { CancelarCitaResult } from '../../cita/components/cancelar-cita-dialog.component';
import {
  BusquedaAsesorDialogComponent,
  AsesorSeleccionado
} from '../../../common/busquedas/busqueda-asesor-dialog.component';
import { AsesorCardComponent } from '../components/asesor-card/asesor-card.component';
import { WeekCalendarComponent } from '../components/week-calendar/week-calendar.component';
import { AgendaDesktopV2Component } from '../components/agenda-desktop-v2/agenda-desktop-v2.component';
import {
  AGENDA_DIALOG_CONTENT_STYLE_V2,
  AGENDA_DIALOG_STYLE_V2,
  AgendaDesktopV2Service
} from '../services/agenda-desktop-v2.service';
import { Persona } from 'src/app/domain/model/persona/persona';
import {
  ProgramacionServicio,
  ProgramacionServicioRequest
} from 'src/app/domain/model/agenda/agenda';
import { AdministrarAgendaFacade } from '../services/administrar-agenda.facade';
import { HasAccessDirective } from 'src/app/common/directives';
import { RECURSO_PROGRAMACION_MASIVA_AGENDA } from 'src/app/common/constants-ejb';

/**
 * Componente de Administración de Agenda
 *
 * Flujo Legacy:
 * 1. Búsqueda de asesor/consultor (por tipo identificación + número)
 * 2. Seleccionar asesor de la tabla
 * 3. Mostrar datos del asesor seleccionado
 * 4. Botones: Ver Agenda, Programación Masiva
 * 5. Ver Agenda abre modal con calendario semanal
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Component({
  selector: 'app-agenda',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    DropdownModule,
    ToastModule,
    TooltipModule,
    TagModule,
    LoadingOverlayComponent,
    CitaDialogComponent,
    BusquedaAsesorDialogComponent,
    AsesorCardComponent,
    WeekCalendarComponent,
    AgendaDesktopV2Component,
    HasAccessDirective
  ],
  providers: [MessageService, AdministrarAgendaFacade],
  template: `
    <div class="p-3 sm:p-4 lg:p-6 max-w-7xl mx-auto overflow-x-hidden">
      <!-- Toast -->
      <p-toast></p-toast>

      <!-- Overlay de carga -->
      <app-loading-overlay [blocked]="loading()" />

      <!-- Header -->
      <div class="mb-4">
        <h1 class="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-1">
          Administrar Agenda
        </h1>
        <p class="text-gray-600 dark:text-gray-400">Selección de consultor</p>
      </div>

      <!-- Botones de acción (siempre visibles según legacy) -->
      <div
        class="mb-4 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-end sm:justify-start gap-2 sm:gap-3"
      >
        <button
          pButton
          label="Ver Agenda"
          icon="pi pi-calendar"
          class="p-button-success w-full sm:w-auto min-h-[44px]"
          (click)="abrirVerAgenda()"
          [disabled]="!asesorSeleccionado()"
        ></button>
        <button
          *hasAccess="recursoProgramacionMasivaAgenda"
          pButton
          label="Programación Masiva"
          icon="pi pi-copy"
          class="p-button-info w-full sm:w-auto min-h-[44px]"
          (click)="abrirProgramacionMasiva()"
        ></button>
      </div>

      <!-- Datos del Consultor -->
      <app-asesor-card
        [asesor]="asesorSeleccionado()"
        [mostrarBotonBuscar]="puedeAdministrarAgendaOtros()"
        (buscar)="abrirModalBusqueda()"
      />

      <!-- Modal: Buscar Asesor (componente self-contained) -->
      @if (dialogBusquedaVisible) {
        <app-asesor-search
          (onSelect)="onAsesorSeleccionado($event)"
          (onClose)="dialogBusquedaVisible = false"
        />
      }

      <!-- Modal: Ver Agenda -->
      <p-dialog
        [(visible)]="dialogAgendaVisible"
        [header]="'Agenda del asesor ' + nombreAsesorSeleccionado()"
        [modal]="true"
        [style]="agendaV2() ? dialogStyleV2 : dialogStyle"
        [styleClass]="agendaV2() ? 'agenda-v2-dialog' : ''"
        [contentStyle]="agendaV2() ? dialogContentStyleV2 : dialogContentStyle"
        [breakpoints]="agendaV2() ? {} : dialogBreakpoints"
        [closable]="true"
      >
        @if (agendaV2()) {
          <app-agenda-desktop-v2
            [anio]="anioSeleccionado()"
            [mes]="mesSeleccionado()"
            [anios]="anios"
            [meses]="meses"
            [nombreMes]="nombreMes()"
            [diasSemanaCortos]="diasSemanaCortos"
            [diasVaciosInicio]="diasVaciosInicio()"
            [diasDelMes]="diasDelMes()"
            [leyenda]="tiposCitaLeyenda"
            [esDiaMarcado]="esDiaMarcadoFn"
            [esHoy]="esHoyFn"
            [citas]="citasSemana()"
            [diasSemana]="diasSemanaActual()"
            [loading]="loadingAgenda()"
            [tooltipText]="tooltips.CALENDAR_CLICK_TO_CREATE"
            (anioChange)="anioSeleccionado.set($event); onMesAnioChange()"
            (mesChange)="mesSeleccionado.set($event); onMesAnioChange()"
            (mesAnterior)="mesAnterior()"
            (mesSiguiente)="mesSiguiente()"
            (diaClick)="seleccionarSemana($event)"
            (semanaAnterior)="irSemanaAnterior()"
            (semanaSiguiente)="irSemanaSiguiente()"
            (hoy)="irSemanaHoy()"
            (citaClick)="verDetalleCita($event)"
            (celdaClick)="onCeldaCalendarioClickFromCalendar($event)"
          />
        } @else {
        <div class="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <!-- Panel Izquierdo: Calendario + Leyenda -->
          <div class="lg:col-span-1">
            <!-- Selector Año/Mes -->
            <div class="flex gap-2 mb-3">
              <div class="flex-1">
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >Año</label
                >
                <p-dropdown
                  [ngModel]="anioSeleccionado()"
                  (ngModelChange)="
                    anioSeleccionado.set($event); onMesAnioChange()
                  "
                  [options]="anios"
                  appendTo="body"
                  [style]="{ width: '100%' }"
                >
                </p-dropdown>
              </div>
              <div class="flex-1">
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >Mes</label
                >
                <p-dropdown
                  [ngModel]="mesSeleccionado()"
                  (ngModelChange)="
                    mesSeleccionado.set($event); onMesAnioChange()
                  "
                  [options]="meses"
                  optionLabel="label"
                  optionValue="value"
                  appendTo="body"
                  [style]="{ width: '100%' }"
                >
                </p-dropdown>
              </div>
            </div>

            <!-- Mini Calendario Mensual -->
            <div class="border dark:border-gray-600 rounded-lg p-2 mb-4">
              <div class="flex justify-between items-center mb-2">
                <button
                  pButton
                  icon="pi pi-chevron-left"
                  class="p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                  pTooltip="Mes anterior"
                  (click)="mesAnterior()"
                ></button>
                <span class="font-semibold text-sm"
                  >{{ nombreMes() }} {{ anioSeleccionado() }}</span
                >
                <button
                  pButton
                  icon="pi pi-chevron-right"
                  class="p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                  pTooltip="Mes siguiente"
                  (click)="mesSiguiente()"
                ></button>
              </div>
              <div class="grid grid-cols-7 gap-0 text-center text-xs">
                @for (dia of diasSemanaCortos; track dia) {
                  <div
                    class="p-1 sm:p-2 font-medium text-gray-600 dark:text-gray-400"
                  >
                    {{ dia }}
                  </div>
                }
                @for (empty of diasVaciosInicio(); track $index) {
                  <div class="p-1 sm:p-2"></div>
                }
                @for (dia of diasDelMes(); track dia) {
                  <div
                    class="p-1 sm:p-2 min-h-[32px] sm:min-h-[36px] flex items-center justify-center cursor-pointer rounded hover:bg-blue-100 dark:hover:bg-blue-900"
                    [class.bg-blue-500]="esDiaEnSemanaSeleccionada(dia)"
                    [class.text-white]="esDiaEnSemanaSeleccionada(dia)"
                    [class.font-bold]="esHoy(dia)"
                    (click)="seleccionarSemana(dia)"
                  >
                    {{ dia }}
                  </div>
                }
              </div>
            </div>

            <!-- Leyenda de colores -->
            <div class="border dark:border-gray-600 rounded-lg p-3">
              <h4 class="font-semibold text-sm mb-2">Leyenda</h4>
              <div class="flex flex-col gap-1 text-xs">
                @for (tipo of tiposCitaLeyenda; track (tipo.label || $index)) {
                  <div class="flex items-center gap-2">
                    <span
                      class="w-4 h-4 rounded"
                      [style.background-color]="tipo.color"
                    ></span>
                    <span>{{ tipo.label }}</span>
                  </div>
                }
              </div>
            </div>
          </div>

          <!-- Panel Derecho: Vista Semanal -->
          <div class="lg:col-span-3">
            <app-week-calendar
              [citas]="citasSemana()"
              [diasSemana]="diasSemanaActual()"
              [loading]="loadingAgenda()"
              [enableMobileStickyHours]="true"
              [tooltipText]="tooltips.CALENDAR_CLICK_TO_CREATE"
              (citaClick)="verDetalleCita($event)"
              (celdaClick)="onCeldaCalendarioClickFromCalendar($event)"
              (semanaAnterior)="irSemanaAnterior()"
              (semanaSiguiente)="irSemanaSiguiente()"
            />
          </div>
        </div>
        }

        <ng-template pTemplate="footer">
          <div
            class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
          >
            <p-button
              label="Cerrar"
              icon="pi pi-times"
              [outlined]="true"
              styleClass="min-h-[44px]"
              (onClick)="dialogAgendaVisible = false"
            >
            </p-button>
          </div>
        </ng-template>
      </p-dialog>

      <!-- Modal: Detalle de Cita - Usando CitaDialogComponent -->
      @if (dialogDetalleCitaVisible) {
        <app-cita-dialog
          #citaDialog
          [cita]="citaSeleccionada()"
          [mode]="citaMode()"
          [tiposCita]="citaMode() === 'create' ? tiposCitaDropdown() : tiposCitaCompletos()"
          [loadingDetalle]="loadingDetalle()"
          (onClose)="cerrarDialogoCita()"
          (onSave)="guardarCita($event)"
          (onCancel)="cancelarCitaHandler($event)"
          (onCumplir)="cumplirCitaHandler($event)"
          (onCumplimientoRegistrado)="onCumplimientoRegistradoHandler($event)"
          (onRegresarDesdeCumplimiento)="onRegresarDesdeCumplimientoHandler()"
        />
      }
    </div>
  `
})
export class AgendaComponent implements OnInit {
  // === Facade (contiene toda la lógica y estado) ===
  protected readonly facade = inject(AdministrarAgendaFacade);

  // === Exponer estado del Facade al template ===
  readonly loading = this.facade.loading;
  readonly loadingAgenda = this.facade.loadingAgenda;
  readonly loadingDetalle = this.facade.loadingDetalle;
  readonly asesorSeleccionado = this.facade.asesorSeleccionado;
  readonly esAsesor = this.facade.esAsesor;
  readonly puedeAdministrarAgendaOtros = this.facade.puedeAdministrarAgendaOtros;
  readonly tiposIdentificacion = this.facade.tiposIdentificacion;
  readonly tiposCitaDropdown = this.facade.tiposCitaDropdown;
  readonly tiposCitaCompletos = this.facade.tiposCitaCompletos;
  readonly citaSeleccionada = this.facade.citaSeleccionada;
  readonly citaMode = this.facade.citaMode;
  readonly citasSemana = this.facade.citasSemana;
  readonly anioSeleccionado = this.facade.anioSeleccionado;
  readonly mesSeleccionado = this.facade.mesSeleccionado;
  readonly nombreMes = this.facade.nombreMes;
  readonly diasVaciosInicio = this.facade.diasVaciosInicio;
  readonly diasDelMes = this.facade.diasDelMes;
  readonly diasSemanaActual = this.facade.diasSemanaActual;

  // Nombre completo del asesor para el header del diálogo
  readonly nombreAsesorSeleccionado = computed(() => {
    const asesor = this.asesorSeleccionado();
    return asesor ? this.facade.getNombreCompleto(asesor) : '';
  });

  // === Exponer constantes del Facade ===
  readonly tooltips = this.facade.tooltips;
  readonly anios = this.facade.anios;
  readonly meses = this.facade.meses;
  readonly diasSemanaCortos = this.facade.diasSemanaCortos;
  readonly horas = this.facade.horas;
  readonly tiposCitaLeyenda = this.facade.tiposCitaLeyenda;
  readonly recursoProgramacionMasivaAgenda = RECURSO_PROGRAMACION_MASIVA_AGENDA;

  // === Agenda desktop v2 (>= 1024 px) ===
  private readonly agendaDesktopV2 = inject(AgendaDesktopV2Service);
  readonly agendaV2 = this.agendaDesktopV2.habilitado;
  readonly esDiaMarcadoFn = (dia: number): boolean =>
    this.esDiaEnSemanaSeleccionada(dia);
  readonly esHoyFn = (dia: number): boolean => this.esHoy(dia);

  readonly dialogStyle = { width: '92vw', maxWidth: '88rem' };
  readonly dialogContentStyle = { 'max-height': '80vh', 'overflow-y': 'auto' };
  readonly dialogBreakpoints = {
    '1280px': '92vw',
    '1024px': '94vw',
    '768px': '96vw'
  };
  readonly dialogStyleV2 = AGENDA_DIALOG_STYLE_V2;
  readonly dialogContentStyleV2 = AGENDA_DIALOG_CONTENT_STYLE_V2;

  // === Variables de template (two-way binding) ===
  dialogBusquedaVisible = false;
  dialogAgendaVisible = false;
  dialogDetalleCitaVisible = false;

  // === Lifecycle ===
  ngOnInit(): void {
    this.facade.init();
    // Sincronizar variables de template con signals del Facade
    this.syncDialogStates();
  }

  private syncDialogStates(): void {
    // Los signals del Facade actualizan las variables de template
    // (Angular detecta cambios automáticamente)
  }

  // === Delegación al Facade ===
  abrirModalBusqueda(): void {
    this.dialogBusquedaVisible = true;
  }

  onAsesorSeleccionado(asesor: AsesorSeleccionado): void {
    const persona: Persona = {
      cdpersona: asesor.cdpersona,
      cdtipo_identificacion: asesor.cdtipoIdentificacion,
      nmidentificacion: asesor.nmidentificacion,
      dsnombre1: asesor.dsnombre1,
      dsnombre2: asesor.dsnombre2,
      dsapellido1: asesor.dsapellido1,
      dsapellido2: asesor.dsapellido2,
      dsnombre_comercial: asesor.dsnombreComercial,
      snactivo: asesor.snactivo
    } as Persona;
    this.facade.seleccionarAsesor(persona);
    this.dialogBusquedaVisible = false;
  }

  abrirProgramacionMasiva(): void {
    this.facade.abrirProgramacionMasiva();
  }

  @ViewChild(WeekCalendarComponent) weekCalendar?: WeekCalendarComponent;
  @ViewChild('citaDialog') citaDialog?: CitaDialogComponent;

  abrirVerAgenda(): void {
    this.facade.abrirVerAgenda();
    this.dialogAgendaVisible = this.facade.dialogAgendaVisible();
    // Scroll a la hora actual después de que el diálogo se renderice
    if (this.dialogAgendaVisible) {
      setTimeout(() => this.weekCalendar?.scrollToCurrentHour(), 400);
    }
  }

  onMesAnioChange(): void {
    this.facade.onMesAnioChange();
  }

  mesAnterior(): void {
    this.facade.mesAnterior();
  }

  mesSiguiente(): void {
    this.facade.mesSiguiente();
  }

  seleccionarSemana(dia: number): void {
    this.facade.seleccionarSemana(dia);
  }

  irSemanaAnterior(): void {
    this.facade.irSemanaAnterior();
  }

  irSemanaSiguiente(): void {
    this.facade.irSemanaSiguiente();
  }

  irSemanaHoy(): void {
    this.facade.irSemanaHoy();
  }

  esDiaEnSemanaSeleccionada(dia: number): boolean {
    return this.facade.esDiaEnSemanaSeleccionada(dia);
  }

  esHoy(dia: number): boolean {
    return this.facade.esHoy(dia);
  }

  esHoyFecha(fechaStr: string): boolean {
    return this.facade.esHoyFecha(fechaStr);
  }

  getCitasEnHora(fecha: string, hora: number): ProgramacionServicio[] {
    return this.facade.getCitasEnHora(fecha, hora);
  }

  hayCitaOcupandoHora(
    fecha: string,
    hora: number
  ): ProgramacionServicio | null {
    return this.facade.hayCitaOcupandoHora(fecha, hora);
  }

  getCitaTooltip(cita: ProgramacionServicio): string {
    return this.facade.getCitaTooltip(cita);
  }

  formatHoraCita(cita: ProgramacionServicio): string {
    return this.facade.formatHoraCita(cita);
  }

  getLightColor(hexColor: string): string {
    return this.facade.getLightColor(hexColor);
  }

  getContrastTextColor(hexColor: string): string {
    return this.facade.getContrastTextColor(hexColor);
  }

  getCitaColor(cita: ProgramacionServicio): string {
    return this.facade.getCitaColor(cita);
  }

  verDetalleCita(cita: ProgramacionServicio): void {
    this.facade.verDetalleCita(cita);
    this.dialogDetalleCitaVisible = true;
  }

  cerrarDialogoCita(): void {
    this.facade.cerrarDialogoCita();
    this.dialogDetalleCitaVisible = false;
  }

  guardarCita(citaRequest: ProgramacionServicioRequest): void {
    this.facade.guardarCita(citaRequest).then((ok) => {
      if (ok) {
        this.dialogDetalleCitaVisible = false;
      }
    });
  }

  cancelarCitaHandler(event: CancelarCitaResult): void {
    this.facade.cancelarCita(event).then(() => {
      this.dialogDetalleCitaVisible = false;
    });
  }

  cumplirCitaHandler(citaId: number): void {
    this.facade.cumplirCita(citaId).then(() => {
      this.dialogDetalleCitaVisible = false;
    });
  }

  /**
   * Handler para cuando se registra un cumplimiento exitosamente desde el modal de cita.
   * Solo refresca las citas sin mostrar mensaje adicional.
   * Se espera un breve delay para asegurar que la transacción del SP se haya commiteado
   * antes de refrescar las citas desde agenda-ms.
   */
  async onCumplimientoRegistradoHandler(citaId: number): Promise<void> {
    // Delay para asegurar commit de SP_ACTUALIZAR_CITA en la BD
    await new Promise((resolve) => setTimeout(resolve, 500));
    await this.facade.cargarCitasSemana();
  }

  async onRegresarDesdeCumplimientoHandler(): Promise<void> {
    await this.facade.cargarCitasSemana();
    this.citaDialog?.finalizarRegresoCumplimiento();
  }

  onCeldaCalendarioClick(
    fecha: string,
    hora: number,
    minuto: number,
    event: Event
  ): void {
    event.stopPropagation();
    this.facade.onCeldaCalendarioClick(fecha, hora, minuto);
    this.dialogDetalleCitaVisible = this.facade.dialogDetalleCitaVisible();
  }

  onCeldaCalendarioClickFromCalendar(event: {
    fecha: string;
    hora: number;
    minuto: number;
  }): void {
    this.facade.onCeldaCalendarioClick(event.fecha, event.hora, event.minuto);
    this.dialogDetalleCitaVisible = this.facade.dialogDetalleCitaVisible();
  }
}
